import { uniq } from 'lodash';
import { Moment } from 'moment';
import CustomerType from '@/contact/constants/CustomerType';
import OptInOption from '@/contact/constants/OptInOption';
import { DEFAULT_LANGUAGE } from '@/shared/constants/Language';
import Country from '@/shared/models/Country';
import Person from '@/shared/models/Person';
import { VatNumber } from '@/shared/models/common';
import { isTruthy } from '@/shared/utils/typescript';

export interface CustomerId {__customerId: number}

export type ContactTarget = string;

export enum ContactTargetCategory {
  EMAIL,
  SMS,
  VOICE
}

export default class Customer extends Person<CustomerId> {
  type = CustomerType.OWNER;
  title: string | null = null;
  dealerCustomerDataId: number | null = null;
  email: ContactTarget = '';
  secondEmail: ContactTarget = '';
  thirdEmail: ContactTarget = '';
  phone: ContactTarget = '';
  secondPhone: ContactTarget = '';
  mobilePhone: ContactTarget = '';
  secondMobilePhone: ContactTarget = '';
  communicationSummary = ''; // Concatenated string of the above communication properties (so that backend can decide what to show)
  address = '';
  houseNumber = '';
  zipCode = '';
  city = '';
  language = DEFAULT_LANGUAGE;
  optinDealerPhone = false;
  optinDealerEmail = false;
  optinDealerSMS = false;
  optinDealerPost = false;
  isActive = true;
  isDeleted = false;
  isCustomer = true;
  optOut = false;
  emailOptIn = false;
  smsOptIn = false;
  country: Country | null = null;
  birthdate: Moment | null = null;
  optOutOn: Moment | null = null;
  emailOptInOn: Moment | null = null;
  smsOptInOn: Moment | null = null;
  vat: VatNumber | null = null;

  static withFullName(fullName: string): Customer {
    return Customer.withData({ fullName });
  }

  static withData(data: Partial<Customer>): Customer {
    return Object.assign(new Customer(), data);
  }

  get emails(): Array<ContactTarget> {
    return uniq([this.email, this.secondEmail, this.thirdEmail].filter(Boolean));
  }

  get phones(): Array<ContactTarget> {
    return uniq([this.phone, this.secondPhone].filter(Boolean));
  }

  get mobilePhones(): Array<ContactTarget> {
    return uniq([this.mobilePhone, this.secondMobilePhone].filter(Boolean));
  }

  get allContactTargets(): Array<ContactTarget> {
    return [this.emails, this.phones, this.mobilePhones].flat();
  }

  getContactTargetsByCategory(category: ContactTargetCategory): Array<ContactTarget> {
    switch (category) {
      case ContactTargetCategory.EMAIL:
        return this.emails;
      case ContactTargetCategory.SMS:
        return this.mobilePhones;
      case ContactTargetCategory.VOICE:
        return this.phones;
      default:
        throw new Error(`Unsupported category: ${category}`);
    }
  }

  hasContactTarget(target: ContactTarget): boolean {
    return this.allContactTargets.includes(target);
  }

  getOptInOptions(): Array<OptInOption> {
    return [
      this.emailOptIn && OptInOption.EMAIL,
      this.smsOptIn && OptInOption.SMS,
    ].filter(isTruthy);
  }
}
